<script setup>
import { useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SettingsHistoryDrawer from '@/components/admin/report-qc/SettingsHistoryDrawer.vue'
import { useUnsavedGuard } from '@/composables/useUnsavedGuard.js'

/**
 * RED-384: Mootori seaded. Every save is a new settings version with a note
 * (design-spec §1, §4). The server is the only judge of the limits: fields are
 * checked live through Precognition, and the worst-case line shows the SAVED
 * values, so the cost formula is never copied into JS.
 */
const props = defineProps({
  settings: { type: Object, required: true },
  bounds: { type: Object, required: true },
  versions: { type: Array, default: undefined },
})

const { t } = useI18n()
const toast = useToast()

const VALUE_FIELDS = [
  'model',
  'max_output_tokens',
  'thinking_level',
  'concurrency',
  'query_timeout_seconds',
  'run_deadline_seconds',
  'prices',
  'prices_checked_on',
  'cost_cap_usd',
  'upload_retention_days',
]

const snapshot = values => {
  const copy = JSON.parse(JSON.stringify(values))
  return Object.fromEntries(VALUE_FIELDS.map(key => [key, copy[key] ?? null]))
}

const form = useForm('put', route('admin.report-qc.settings.update'), { ...snapshot(props.settings.values), note: '' })
form.setValidationTimeout(400)

const historyOpen = ref(false)

const valuesChanged = computed(
  () => JSON.stringify(snapshot(form.data())) !== JSON.stringify(snapshot(props.settings.values)),
)

useUnsavedGuard(() => valuesChanged.value || form.note.trim() !== '')

// A save or a restore brings new props: the saved values become the new baseline.
watch(
  () => props.settings.values,
  values => {
    form.defaults({ ...snapshot(values), note: '' })
    form.reset()
    form.clearErrors()
  },
  { deep: true },
)

// Live checks. The cost cap depends on the model, its prices and the answer
// cap, so it is asked again whenever one of them changes.
const validateLive = (...fields) => fields.forEach(field => form.validate(field))
watch(() => form.max_output_tokens, () => validateLive('max_output_tokens', 'cost_cap_usd'))
watch(() => form.concurrency, () => validateLive('concurrency'))
watch(() => form.query_timeout_seconds, () => validateLive('query_timeout_seconds'))
watch(() => form.run_deadline_seconds, () => validateLive('run_deadline_seconds', 'query_timeout_seconds'))
watch(() => form.cost_cap_usd, () => validateLive('cost_cap_usd'))
watch(() => form.upload_retention_days, () => validateLive('upload_retention_days'))

// RED-375: the prune runs once a night, so N days on the page means up to N + 1.
// Only for a value the server accepts: the hint never promises a refused number.
const uploadRetentionDaysMax = computed(() => {
  const days = form.upload_retention_days
  return Number.isInteger(days) && days >= props.bounds.uploadRetentionDaysMin && days <= props.bounds.uploadRetentionDaysMax
    ? days + 1
    : null
})
watch(() => form.model, () => validateLive('model', 'cost_cap_usd'))
watch(() => JSON.stringify(form.prices?.[form.model] ?? {}), () => validateLive('cost_cap_usd'))

const thinkingOptions = computed(() =>
  props.bounds.thinkingLevels.map(level => ({ value: level, label: t(`messages.admin.report_qc.settings.thinking.${level}`) })),
)

const priceFields = ['input', 'cached_input', 'output']
const priceError = field => form.errors[`prices.${form.model}.${field}`]
const priceLabel = field => t(`messages.admin.report_qc.settings.price_${field}`)

// The server keeps the day as YYYY-MM-DD; the picker wants a local Date.
const pricesCheckedOn = computed({
  get: () => {
    const [year, month, day] = String(form.prices_checked_on ?? '').split('-').map(Number)
    return year && month && day ? new Date(year, month - 1, day) : null
  },
  set: date => {
    form.prices_checked_on = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : null
    form.validate('prices_checked_on')
  },
})

const usd = value => new Intl.NumberFormat('et-EE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
const tokens = value => new Intl.NumberFormat('et-EE').format(value)

const save = () => {
  form.put(route('admin.report-qc.settings.update'), {
    preserveScroll: true,
    onSuccess: page => {
      toast.add({
        severity: 'success',
        summary: t('messages.admin.report_qc.settings.saved'),
        detail: t('messages.admin.report_qc.settings.saved_detail', { n: page.props.settings.versionNumber }),
        life: 4000,
      })
    },
  })
}
</script>

<template>
  <div class="flex flex-col gap-6" data-testid="engine-settings">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-xl font-medium">{{ t('messages.admin.report_qc.nav.seaded') }}</h2>
        <p v-if="settings.versionNumber" class="text-sm text-gray-600" data-testid="settings-version">
          {{ t('messages.admin.report_qc.settings.version', { n: settings.versionNumber }) }}
        </p>
      </div>
      <Button
        :label="t('messages.admin.report_qc.history.button')"
        icon="pi pi-history"
        severity="secondary"
        outlined
        data-testid="settings-history-open"
        @click="historyOpen = true"
      />
    </div>

    <Message v-if="!settings.versionNumber" severity="info" :closable="false">
      {{ t('messages.admin.report_qc.settings.no_version_yet') }}
    </Message>

    <Card>
      <template #content>
        <Fluid>
          <div class="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
            <div>
              <label class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.vendor') }}</label>
              <div class="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                <i class="pi pi-lock text-gray-400" aria-hidden="true" />
                <span>{{ settings.vendor }}</span>
              </div>
              <p class="mt-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.settings.vendor_hint') }}</p>
            </div>

            <div>
              <label for="rqc-model" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.model') }}</label>
              <Select
                v-model="form.model"
                input-id="rqc-model"
                :options="settings.models"
                :invalid="!!form.errors.model"
                data-testid="settings-model"
              />
              <p v-if="form.errors.model" class="mt-1 text-sm text-red-600">{{ form.errors.model }}</p>
            </div>

            <div class="md:col-span-2">
              <label class="mb-1 block text-sm font-medium" id="rqc-thinking-label">{{ t('messages.admin.report_qc.settings.thinking_level') }}</label>
              <SelectButton
                v-model="form.thinking_level"
                :options="thinkingOptions"
                option-label="label"
                option-value="value"
                :allow-empty="false"
                aria-labelledby="rqc-thinking-label"
                class="w-auto! flex-wrap"
                data-testid="settings-thinking"
              />
              <p class="mt-1 text-xs text-gray-500">
                {{ t('messages.admin.report_qc.settings.thinking_hint') }}
              </p>
              <p v-if="form.errors.thinking_level" class="mt-1 text-sm text-red-600">{{ form.errors.thinking_level }}</p>
            </div>

            <div>
              <label for="rqc-max-output" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.max_output_tokens') }}</label>
              <InputNumber
                v-model="form.max_output_tokens"
                input-id="rqc-max-output"
                :min="0"
                :use-grouping="true"
                locale="et-EE"
                :invalid="!!form.errors.max_output_tokens"
                data-testid="settings-max-output-tokens"
              />
              <p class="mt-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.settings.max_output_tokens_hint') }}</p>
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.max_output_tokens }">{{ form.errors.max_output_tokens || '&nbsp;' }}</p>
            </div>

            <div>
              <label for="rqc-concurrency" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.concurrency') }}</label>
              <InputNumber
                v-model="form.concurrency"
                input-id="rqc-concurrency"
                :min="0"
                :invalid="!!form.errors.concurrency"
                data-testid="settings-concurrency"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.concurrency }">{{ form.errors.concurrency || '&nbsp;' }}</p>
            </div>

            <div>
              <label for="rqc-query-timeout" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.query_timeout_seconds') }}</label>
              <InputNumber
                v-model="form.query_timeout_seconds"
                input-id="rqc-query-timeout"
                :min="0"
                :use-grouping="false"
                :invalid="!!form.errors.query_timeout_seconds"
                data-testid="settings-query-timeout"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.query_timeout_seconds }">{{ form.errors.query_timeout_seconds || '&nbsp;' }}</p>
            </div>

            <div>
              <label for="rqc-run-deadline" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.run_deadline_seconds') }}</label>
              <InputNumber
                v-model="form.run_deadline_seconds"
                input-id="rqc-run-deadline"
                :min="0"
                :use-grouping="false"
                :invalid="!!form.errors.run_deadline_seconds"
                data-testid="settings-run-deadline"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.run_deadline_seconds }">{{ form.errors.run_deadline_seconds || '&nbsp;' }}</p>
            </div>

            <div class="md:col-span-2">
              <div class="flex items-center gap-3">
                <ToggleSwitch :model-value="settings.maskPersonalCodes" disabled input-id="rqc-masking" data-testid="settings-masking" />
                <label for="rqc-masking" class="text-sm font-medium">
                  <i class="pi pi-lock mr-1 text-xs text-gray-500" aria-hidden="true" data-testid="settings-masking-lock" />{{ t('messages.admin.report_qc.settings.masking') }}
                </label>
                <span class="text-sm text-gray-500">
                  {{ settings.maskPersonalCodes ? t('messages.admin.report_qc.settings.masking_on') : t('messages.admin.report_qc.settings.masking_off') }}
                </span>
              </div>
              <p class="mt-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.settings.masking_hint') }}</p>
            </div>
          </div>
        </Fluid>
      </template>
    </Card>

    <Card data-testid="settings-retention">
      <template #title>
        <span class="text-lg font-medium">{{ t('messages.admin.report_qc.settings.retention_heading') }}</span>
      </template>
      <template #content>
        <Fluid>
          <div class="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
            <div>
              <label for="rqc-upload-retention" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.upload_retention_days') }}</label>
              <InputNumber
                v-model="form.upload_retention_days"
                input-id="rqc-upload-retention"
                :min="bounds.uploadRetentionDaysMin"
                :max="bounds.uploadRetentionDaysMax"
                :use-grouping="false"
                :invalid="!!form.errors.upload_retention_days"
                data-testid="settings-upload-retention"
              />
              <p class="mt-1 text-xs text-gray-500" data-testid="settings-upload-retention-hint">
                {{
                  uploadRetentionDaysMax
                    ? t('messages.admin.report_qc.settings.upload_retention_hint', { max: uploadRetentionDaysMax })
                    : t('messages.admin.report_qc.settings.upload_retention_hint_empty')
                }}
              </p>
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.upload_retention_days }" data-testid="settings-upload-retention-error">{{ form.errors.upload_retention_days || '&nbsp;' }}</p>
            </div>
          </div>
        </Fluid>
      </template>
    </Card>

    <Card>
      <template #title>
        <span class="text-lg font-medium">{{ t('messages.admin.report_qc.settings.prices_heading') }}</span>
      </template>
      <template #content>
        <Fluid>
          <p class="mb-3 text-sm text-gray-600">{{ form.model }} · {{ t('messages.admin.report_qc.settings.prices_unit') }}</p>
          <div v-if="form.prices?.[form.model]" class="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-3">
            <div v-for="field in priceFields" :key="field">
              <label :for="`rqc-price-${field}`" class="mb-1 block text-sm font-medium">{{ priceLabel(field) }}</label>
              <InputNumber
                v-model="form.prices[form.model][field]"
                :input-id="`rqc-price-${field}`"
                mode="decimal"
                locale="et-EE"
                :min-fraction-digits="2"
                :max-fraction-digits="6"
                :invalid="!!priceError(field)"
                :data-testid="`settings-price-${field}`"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !priceError(field) }">{{ priceError(field) || '&nbsp;' }}</p>
            </div>
          </div>

          <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
            <div>
              <label for="rqc-prices-checked-on" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.prices_checked_on') }}</label>
              <DatePicker
                v-model="pricesCheckedOn"
                input-id="rqc-prices-checked-on"
                date-format="dd.mm.yy"
                :max-date="new Date()"
                show-icon
                :invalid="!!form.errors.prices_checked_on"
                data-testid="settings-prices-checked-on"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.prices_checked_on }">{{ form.errors.prices_checked_on || '&nbsp;' }}</p>
            </div>

            <div>
              <label for="rqc-cost-cap" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.cost_cap_usd') }}</label>
              <InputNumber
                v-model="form.cost_cap_usd"
                input-id="rqc-cost-cap"
                mode="decimal"
                locale="et-EE"
                :min-fraction-digits="2"
                :max-fraction-digits="2"
                :invalid="!!form.errors.cost_cap_usd"
                data-testid="settings-cost-cap"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.cost_cap_usd }" data-testid="settings-cost-cap-error">{{ form.errors.cost_cap_usd || '&nbsp;' }}</p>
            </div>
          </div>

          <div v-if="settings.worstCase" class="mt-2 border-l-2 border-blue-300 pl-3 text-sm text-gray-700" data-testid="settings-worst-case">
            <p>
              {{
                t('messages.admin.report_qc.settings.worst_case', {
                  usd: usd(settings.worstCase.questionUsd),
                  cap: usd(settings.worstCase.capUsd ?? 0),
                  n: settings.worstCase.questionsThatFit,
                })
              }}
            </p>
            <p class="mt-1 text-xs text-gray-500">
              {{ t('messages.admin.report_qc.settings.worst_case_basis', { tokens: tokens(bounds.referenceInputTokens) }) }}
            </p>
          </div>
        </Fluid>
      </template>
    </Card>

    <Card>
      <template #content>
        <Fluid>
          <label for="rqc-note" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.note') }}</label>
          <Textarea
            v-model="form.note"
            input-id="rqc-note"
            id="rqc-note"
            rows="2"
            auto-resize
            :maxlength="bounds.noteMax"
            :invalid="!!form.errors.note"
            data-testid="settings-note"
          />
          <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.note }" data-testid="settings-note-error">{{ form.errors.note || '&nbsp;' }}</p>
          <div class="mt-2 flex justify-end">
            <Button
              :label="t('messages.admin.report_qc.settings.save')"
              icon="pi pi-check"
              class="w-auto!"
              :loading="form.processing"
              :disabled="!valuesChanged || form.processing"
              data-testid="settings-save"
              @click="save"
            />
          </div>
        </Fluid>
      </template>
    </Card>

    <SettingsHistoryDrawer v-model:visible="historyOpen" :versions="versions" />
  </div>
</template>
